/* eslint-disable jsx-a11y/click-events-have-key-events */
/** @jsx jsx */
import React, { useEffect, useMemo, useState } from 'react';
import { css, jsx } from '@emotion/core';
import BlockUI from 'components/molecules/BlockUi';
import moment from 'moment';
import FormContents from 'components/atoms/Form/FormContents';
import Table from 'components/molecules/Table';
import CalendarForm from 'components/molecules/CalendarForm';
import FormField from 'components/atoms/Form/FormField';
import FormLabel from 'components/atoms/Form/FormLabel';
import BodyText from 'components/atoms/BodyText';
import HourInput from 'components/atoms/Form/HourInput';
import FormSubmitArea from 'components/atoms/Form/FormSubmitArea';
import { PrimaryButton, DeleteButton } from 'components/atoms/Button';
import TextForm from 'components/molecules/TextForm';
import ConfirmModal from 'components/organismos/ConfirmModal';
import useAttendAgreement36Application from './hooks'
import FormPadding from 'components/atoms/Form/FormPadding';
import AttendAgreement36ApplicationDomain from 'domain/master/attend/attendAgreement36Application';
import { Column } from 'react-table';
import { grayScale, textColor, textFontSize } from 'components/styles';
import { Col, Row } from 'react-grid-system';
import TabControl from 'components/atoms/TabControl';
import AttendAgreement36Modal from 'components/molecules/ChangeLogModal/Template/AttendAgreement36Modal';
import HistoryApprovalTable from 'components/molecules/HistoryApprovalTable';
import FormHeader from 'components/templates/FormHeader';
import FlexBoxItem from 'components/atoms/FlexBoxItem';
import ClickableIconButton from 'components/atoms/ClickableIconButton';
import Icon from 'components/atoms/Icon';
import FlexBox from 'components/atoms/FlexBox';
import CalendarMonth from 'components/molecules/CalendarV1/CalendarMonth';

const styles = {
  approvalWrap: css({
    padding: '0px',
    marginBottom: '15px',
    marginTop: '0px'
  }),
  formContent: css({
    paddingBottom: "20px"
  }),
  fieldForm: css({
    marginLeft: '15px',
    paddingTop: '0px'
  }),
  labelForm: css({
    width: '100px',
    paddingTop: '15px'
  })
}

const AttendAgreement36Application: React.FC = () => {
  // アップロードファイル
  const columns: Array<Column<AttendAgreement36ApplicationDomain>> = useMemo(() => [
    {
      Header: 'No',
      accessor: 'version',
    },
    {
      Header: '申請種別',
      id: 'modifiyStatus',
      accessor: 'modifiyStatus',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        if (cell.row.original.modifiyStatus === 0) {
          return '申請';
        }
        if (cell.row.original.modifiyStatus === 1) {
          return '削除';
        }
        if (cell.row.original.modifiyStatus !== 0 && cell.row.original.modifiyStatus !== 1) {
          return '';
        }
      },
    },
    {
      Header: '日付',
      id: 'targetDate',
      accessor: 'targetDate',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        if (cell.row.original.targetDate != null) {
          return moment(cell.row.original.targetDate).format('YYYY/MM/DD(ddd)');
        }
      },
    },
    {
      Header: '見込み残業時間',
      id: 'applicationOvertime',
      accessor: 'applicationOvertime',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        return cell.row.original.applicationOvertime + "時間";
      },
    },
    {
      Header: '申請日時',
      id: 'applicationDate',
      accessor: 'applicationDate',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        if (cell.row.original.applicationDate != null) {
          return moment(cell.row.original.applicationDate).format('YYYY/MM/DD HH:mm:ss');
        }
        if (cell.row.original.applicationDate == null) {
          return '';
        }
      },
    },
    {
      Header: '申請者',
      id: 'applicationStaffCode',
      accessor: 'applicationStaffCode',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        return (
          <span>
            <div>{cell.row.original.applicationStaffCode}<br></br>{cell.row.original.applicationStaffName}</div>
          </span>
        );
      },
    },
    {
      Header: '申請理由',
      accessor: 'applicationReason',
    },
    {
      Header: '決裁状況',
      id: 'applicationStatus',
      accessor: 'applicationStatus',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        if (cell.row.original.applicationStatus === 0) {
          return '申請中';
        }
        if (cell.row.original.applicationStatus === 1) {
          return '承認';
        }
        if (cell.row.original.applicationStatus === 2) {
          return '差戻';
        }
        if (cell.row.original.applicationStatus !== 0 && cell.row.original.applicationStatus !== 1 && cell.row.original.applicationStatus !== 2) {
          return '';
        }
      },
    },
    {
      Header: '決済日時',
      id: 'approvalDate',
      accessor: 'approvalDate',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        if (cell.row.original.approvalDate != null) {
          return moment(cell.row.original.approvalDate).format('YYYY/MM/DD HH:mm:ss');
        }
        if (cell.row.original.approvalDate == null) {
          return '';
        }
      },
    },
    {
      Header: '差戻コメント',
      accessor: 'rejectReason',
    },
  ], []);

  const sortBy = React.useMemo(
    () => [
      {
        id: 'no',
        desc: false,
      },
    ],
    [],
  );

  const {
    formik,
    orgTreesOptions,
    targetDate,
    setTargetDate,
    toastMessage,
    toastModalOpen,
    setToastModalOpen,
    confirmApplyModalOpen,
    closeConfirmApplyModal,
    isLoading,
    dispStaffName,
    setSelectOrgCode,
    isGetOvertimeSucceed,
    selectChangelog,
    setSelectChangelog,
    fetchChangeLogAgreement36
  } = useAttendAgreement36Application()

  const [changeLogData, setChangeLogData] = useState<Array<any>>([]);

  useEffect(() => {
    if (formik.values.orgCode || formik.values.targetDate || formik.values.staffCode) {
      fetchChangeLogAgreement36(formik.values.orgCode, formik.values.targetDateStr, formik.values.staffCode).then(
        res => {
          setChangeLogData(res);
        }
      );
    }
  }, [formik.values.orgCode, formik.values.targetDate, formik.values.staffCode]);

  return (
    <BlockUI blocking={isLoading}>
      <FormHeader title={"限度時間超過申請"}/>
      <FormContents customStyle={css({paddingBottom: '25px', paddingTop: '15px', marginTop:'60px'})}>      
        <FormField customStyle={styles.fieldForm}>
          <FormLabel label="対象月" customStyle={styles.labelForm} />
          <FlexBox>
           <FlexBoxItem>
              <CalendarMonth
                openActionChevron={true}
                date={targetDate}
                setDate={setTargetDate}
              />
           </FlexBoxItem>
            {/* <FlexBoxItem>
              <ClickableIconButton onClick={() => {
                setTargetDate(new Date(moment(targetDate).subtract(1, 'month').format('YYYY/MM/01')));
              }} additionalCss={css({ marginLeft: '-35px' })}>
                <Icon type="chevronLeft" color="" size="24px" />
              </ClickableIconButton>
            </FlexBoxItem>
            <FlexBoxItem width="160px" customStyle={css({paddingTop: '0px'})}>
              <CalendarForm
                required={true}
                label=""
                date={targetDate}
                dateFormat="YYYY/MM"
                changeDate={(date: Date) => {
                  if (moment(targetDate).format('YYYY-MM') !== moment(date).format('YYYY-MM')) {
                    setTargetDate(date);
                  }
                }} />
            </FlexBoxItem>
            <FlexBoxItem>
              <ClickableIconButton onClick={() => {
                setTargetDate(new Date(moment(targetDate).add(1, 'month').format('YYYY/MM/01')));
              }}>
                <Icon type="chevronRight" color="" size="24px" />
              </ClickableIconButton>
            </FlexBoxItem> */}
          </FlexBox>
        </FormField>
        <FormField customStyle={styles.fieldForm}>
          <FormLabel label="所属組織" customStyle={styles.labelForm} />
          <BodyText customStyle={css({ color: grayScale.gray100, marginTop: '15px' })}>
            {`${sessionStorage.getItem('loginUser.orgCode')} ${sessionStorage.getItem('loginUser.orgName')}`}
          </BodyText>
        </FormField>
        <FormField customStyle={styles.fieldForm}>
          <FormLabel label="スタッフ" customStyle={styles.labelForm} />
          <BodyText customStyle={css({ color: grayScale.gray100, marginTop: '15px' })}>
            {`${sessionStorage.getItem('loginUser.staffCode')} ${sessionStorage.getItem('loginUser.staffName')}`}
          </BodyText>
        </FormField>
        <div className='table-padding' css={css`
          margin-top: 15px;
          td {
            text-align: center;
          }
          input:read-only {
            background-color: #F5F5F5;
            }
          `}>
          <Table>
            <thead>
              <tr>
                <Table.HeaderCell>
                  対象月
                  </Table.HeaderCell>
                <Table.HeaderCell>
                  見込み残業時間
                  </Table.HeaderCell>
                <Table.HeaderCell colSpan={4}>
                  申請理由
                  </Table.HeaderCell>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Table.Cell>
                  {moment(targetDate).format('YYYY年MM月')}
                </Table.Cell>
                <Table.Cell>
                  <HourInput
                    name="applicationExpectedOvertimeHoursStr"
                    value={formik.values.applicationExpectedOvertimeHoursStr || 0}
                    label="時間"
                    min={0}
                    onChange={formik.handleChange}
                    wrapInput={css({
                      width: '120px',
                      background: 'white',
                      margin: 'auto'
                    })}
                    readOnly={formik.values.applicationStatus !== null && Number(formik.values.applicationStatus) === 0}
                  />
                </Table.Cell>
                <Table.Cell colSpan={4}>
                  <TextForm
                    required={false}
                    name="applicationReason"
                    label=""
                    value={String(formik.values.applicationReason)}
                    onChange={formik.handleChange}
                    errorMsg={formik.errors.applicationReason}
                    disable={formik.values.applicationStatus !== null && Number(formik.values.applicationStatus) === 0}
                  />
                </Table.Cell>
              </tr>
            </tbody>
          </Table>
        </div>
        <FormSubmitArea>
          <div
            css={css`
              .btnAction:nth-of-type(2) {
                margin-left: 10px;
              }
              .btnAction:nth-of-type(3) {
                margin-left: 10px;
              }
              margin-bottom: -30px;
            `}>
            <FormField customStyle={css({
              justifyContent: 'center',
              marginTop: '25px'
            })}>
              {
                (isGetOvertimeSucceed && (formik.values.modifiyStatus === null || formik.values.modifiyStatus === 0
                  || (formik.values.modifiyStatus === 1 && formik.values.applicationStatus !== 0))) && (
                  <div className="btnAction">
                    <PrimaryButton
                      ghost={false}
                      disabled={formik.values.applicationStatus === 0 && formik.values.createDate !== null}
                      text="申請する"
                      onClick={() => {
                        formik.handleSubmit();
                        formik.setFieldValue('buttonType', 0);
                      }}
                    />
                  </div>
                )
              }
              {
                formik.values.applicationStatus === 0 && (
                  <div className="btnAction">
                    <DeleteButton
                      ghost={false}
                      text="申請キャンセル"
                      onClick={() => {
                        formik.handleSubmit();
                        formik.setFieldValue('buttonType', 1);
                      }}
                    />
                  </div>
                )
              }
              {
                formik.values.deleteAvailable === true && (
                  <div className="btnAction">
                    <DeleteButton
                      ghost={false}
                      text="削除申請"
                      onClick={() => {
                        formik.handleSubmit();
                        formik.setFieldValue('buttonType', 2);
                      }}
                    />
                  </div>
                )
              }
            </FormField>
            <FormField customStyle={css({justifyContent: 'center'})}>
              <PrimaryButton
                text="キャンセル"
                ghost={true}
                // eslint-disable-next-line no-restricted-globals
                onClick={() => history.back()}
              />
            </FormField>
          </div>
        </FormSubmitArea>
        <ConfirmModal
          open={confirmApplyModalOpen}
          closeHandler={closeConfirmApplyModal}
          onSubmit={formik.handleSubmit}
          // eslint-disable-next-line no-nested-ternary
          title={formik.values.buttonType === 0 ? (formik.values.modifiyStatus === 0 ? '超過修正を申請' : '超過を申請')
            : formik.values.buttonType === 1 ? '超過キャンセルを申請' : '削除超過を申請'}
          // eslint-disable-next-line no-nested-ternary
          content={formik.values.buttonType === 0 ? (formik.values.modifiyStatus === 0 ? '超過修正を申請します。よろしいですか？' : '超過を申請します。よろしいですか？')
            : formik.values.buttonType === 1 ? '超過キャンセルを申請します。よろしいですか？' : '削除超過を申請します。よろしいですか？'}
          // submitText={formik.values.buttonType === 0 ? '申請'
          //   : formik.values.buttonType === 1 ? '申請キャンセル' : '削除申請'}
          submitText='申請する'
          typeSubmit={formik.values.buttonType === 0 ? 'confirm' : 'reject'}
        />
      </FormContents>

      <div style={{ paddingTop: 20 }}>
        <FormContents customStyle={styles.formContent}>
          <Row style={{ padding: "10px 30px" }}>
            <Col
              md={2}
              xs={12}
              style={{
                fontWeight: "bold",
                fontSize: textFontSize.md,
                lineHeight: "24px",
                paddingBottom: "20px",
                color: textColor.main
              }}>
              更新履歴
                        </Col>
          </Row>
          <TabControl
            isTabModal={true}
            isFixed={false}
            items={changeLogData.map((log, index: number) => {
              return {
                value: String(index),
                display: `ver${log.version}`
              };
            })}
            setValue={setSelectChangelog as (arg: string) => void}
            currentValue={selectChangelog}
            styleNotActive={css({ borderRadius: 0, border: "none", width: "100px", flex: "unset" })}
            styleActive={css({
              borderRadius: 0,
              border: "none",
              borderBottom: "1px solid #007BC3",
              width: "100px",
              flex: "unset"
            })}
          />
          {changeLogData.length > 0 &&
            changeLogData.map((itemLog, index: number) => (
              <div style={{ display: selectChangelog !== String(index) ? "none" : "block" }}>
                <React.Fragment>
                  <FormPadding customStyle={{ fontSize: "14px", color: textColor.mobileTitle }}>
                    <Row>
                      <Col
                        md={2}
                        xs={12}
                        style={{
                          fontWeight: "bold",
                          fontSize: textFontSize.re,
                          lineHeight: "24px",
                          paddingBottom: "20px",
                          color: textColor.main
                        }}>
                        申請種別:
                                            </Col>
                      <Col md={10} xs={12}>
                        {itemLog.modifiyStatus == 0 ? "申請" : "削除"}
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        md={2}
                        xs={12}
                        style={{
                          fontWeight: "bold",
                          fontSize: textFontSize.re,
                          lineHeight: "24px",
                          paddingBottom: "20px",
                          color: textColor.main
                        }}>
                        申請者:
                                            </Col>
                      <Col md={10} xs={12}>
                        {itemLog.applicationStaffCode && `${itemLog.applicationStaffCode} `}
                        {itemLog.applicationStaffName && itemLog.applicationStaffName}
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        md={2}
                        xs={12}
                        style={{
                          fontWeight: "bold",
                          fontSize: textFontSize.re,
                          lineHeight: "24px",
                          paddingBottom: "20px",
                          color: textColor.main
                        }}>
                        申請日時:
                                            </Col>
                      <Col md={10} xs={12}>
                        {itemLog.applicationDate && moment(itemLog.applicationDate).format("YYYY/MM/DD HH:mm:ss")}
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        md={2}
                        xs={12}
                        style={{
                          fontWeight: "bold",
                          fontSize: textFontSize.re,
                          lineHeight: "24px",
                          paddingBottom: "20px",
                          color: textColor.main
                        }}>
                        出勤組織:
                                            </Col>
                      <Col md={10} xs={12}>
                        {itemLog.orgCode && itemLog.orgCode}
                        {itemLog.orgName && itemLog.orgName}
                      </Col>
                    </Row>
                    <Row>
                      <AttendAgreement36Modal
                        changeLogData={[itemLog]}
                        selectChangelog={String(index)}
                        customStyle={styles.approvalWrap}
                      />
                    </Row>
                    <Row>
                      <Col
                        md={2}
                        xs={12}
                        style={{
                          fontWeight: "bold",
                          fontSize: textFontSize.re,
                          lineHeight: "24px",
                          paddingBottom: "10px",
                          color: textColor.main
                        }}>
                        修正理由:
                                            </Col>
                      <Col md={10} xs={12}>
                        {itemLog.applicationReason && itemLog.applicationReason}
                      </Col>
                    </Row>
                    {changeLogData.length > 0 && (
                      <Row>
                        <HistoryApprovalTable
                          historyData={itemLog.approvalHistoryList}
                          applicationStatus={itemLog.applicationStatus}
                        />
                      </Row>
                    )}
                  </FormPadding>
                </React.Fragment>
              </div>
            ))}
        </FormContents>
      </div>
    </BlockUI>
  );
};

export default AttendAgreement36Application;